import { ITableauWorkbook, IErrorResponse, IWorkbook, ITag, IWorkbooklog } from '@interfaces';
import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ExceptionService } from '@base/exceptions.service';
import { EMPTY } from '@base/utils';

import { moduleRoutes } from './urlmap';
import { ApplicationHttpClient } from '@base/http-client';
import { TableauService } from '@tableau/tableau.service';
import { IRequestOptions } from '@base/http-client';
import { View, Workbook } from '@models';

const VIZ_EXPORT = {
  PDF: 'pdf',
  IMAGE: 'image',
  CSV: 'csv',
  DATA: 'data'
};

@Injectable()
export class WorkbookService {
  private mapRoutes = moduleRoutes;

  private processingData = false;
  private bsProcessingData = new BehaviorSubject<boolean>(this.processingData);

  private workbooksState: ITableauWorkbook[] = [];
  private bsWorkbooksState = new BehaviorSubject<ITableauWorkbook[]>(this.workbooksState);

  private tableauWorkbooks: ITableauWorkbook[] = [];
  private bsTableauWorkbooks = new BehaviorSubject<ITableauWorkbook[]>(this.tableauWorkbooks);

  private workbookSelected: IWorkbook = EMPTY;
  private bsWorkbookSelected = new BehaviorSubject<IWorkbook>(this.workbookSelected);
  private bsSnapshot = new BehaviorSubject<string>(null);

  private _finalUrl: string;
  private _viz: any;

  constructor(
    private http: ApplicationHttpClient,
    private exceptionService: ExceptionService,
    private tableauService: TableauService,
  ) {
    this._finalUrl = '';
    this.tableauWorkbooks = [];
    this.workbooksState = [];
    this.processingData = false;
    this.bsProcessingData.next(false);
  }

  private getWorkbooks(queryParams: object = null) {
    this.processingData = true;
    this.bsProcessingData.next(this.processingData);

    return this.http.Get(this.mapRoutes.list, queryParams).subscribe(
      (res: ITableauWorkbook[]) => {
        this.processingData = false;
        this.bsProcessingData.next(this.processingData);
        this.workbooksState = res['results'];
        this.bsWorkbooksState.next(this.workbooksState);
      },
      (err: HttpErrorResponse) => {const error: IErrorResponse = this.exceptionService.errorHandling(err); }
    );
  }

  observeWorkbooks(): Observable<ITableauWorkbook[]> {
    return this.bsWorkbooksState.asObservable();
  }

  synchronizeTableau(): Observable<ITableauWorkbook[]> {
    this.processingData = true;
    this.bsProcessingData.next(this.processingData);

    return this.http.Get(this.mapRoutes.sync).pipe(
      map((res: ITableauWorkbook[]) => {
        this.getWorkbooks();
        this.tableauService.setWorkbooks(res);
        this.tableauWorkbooks = res;
        this.bsTableauWorkbooks.next(this.tableauWorkbooks);
        return res;
      },
      (err: HttpErrorResponse) => {
        console.log('REPORTES ERROR');
        const error: IErrorResponse = this.exceptionService.errorHandling(err);
      })
    );
  }

  getPreviewImage(w: ITableauWorkbook | object): Observable<object> {
    this.processingData = true;
    this.bsProcessingData.next(this.processingData);
    const reqOpt: IRequestOptions = {
      responseType: 'blob'
    };

    const endpoint = `${this.mapRoutes.list}${w['id']}/refresh-preview-image/`;
    return this.http.Get(endpoint, null);
  }

  setWorkbook(w: IWorkbook) {
    this.workbookSelected = w;
    this.bsWorkbookSelected.next(this.workbookSelected);
  }

  observeWorkbookSelected(): Observable<IWorkbook> {
    return this.bsWorkbookSelected.asObservable();
  }

  getViews(wbId: number): Observable<any> {
    if (wbId !== EMPTY) {
      this.processingData = true;
      this.bsProcessingData.next(this.processingData);

      const endpoint = `${this.mapRoutes.list}${wbId}/views/`;
      return this.http.Get(endpoint).pipe(
        map(res => {
          this.processingData = false;
          this.bsProcessingData.next(this.processingData);
          return res;
        }),
        catchError((err: HttpErrorResponse) => throwError(err))
      );
    }
    return of([]);
  }

  setFinalUrl(url: string) {
    this._finalUrl = url;
  }

  getFinalUrl() {
    return this._finalUrl;
  }

  observeProcessingData(): Observable<boolean> {
    return this.bsProcessingData.asObservable();
  }

  setStatusProcessingData(status: boolean) {
    this.processingData = status;
    this.bsProcessingData.next(this.processingData);
  }

  setViz(viz: any) {
    this._viz = viz;
  }

  exportData(dataType: string) {
    if (dataType === VIZ_EXPORT.PDF) {this._viz.showExportPDFDialog(); }
    if (dataType === VIZ_EXPORT.IMAGE) {this._viz.showExportImageDialog(); }
    if (dataType === VIZ_EXPORT.CSV) {this._viz.showExportCrossTabDialog(); }
    if (dataType === VIZ_EXPORT.DATA) {this._viz.showExportDataDialog(); }
  }

  updateTag(w: IWorkbook, tag: ITag, action: string) {
    const body = {
      tag: tag.id,
      action: action,
    };
    const finalUrl = `${this.mapRoutes.list}${w.id}/update-tag/`;
    return this.http.Post(finalUrl, body);
  }

  workbooksTags(ids: number[]) {
    return this.http.Post(`${this.mapRoutes.list}workbooks-tags/`, {ids: ids}).pipe(
      map((tags: []) => {
        let finalTags = [];
        tags.map(t => {
          const tag = t;
          tag['id'] = t['tag_id'];
          finalTags = [...finalTags, tag];
          delete tag['tag_id'];
        });
        return finalTags;
      }),
      catchError((err: HttpErrorResponse) => {
        return [];
      })
    );
  }

  registerLog(workbooklog: IWorkbooklog): Observable<IWorkbooklog> {
    const payload = {
      user: workbooklog.user.id,
      workbook: workbooklog.workbook.id,
    };
    return this.http.Post(this.mapRoutes.workbooklogs, payload);
  }

  getDefaultView(wbId: number): Observable<View> {
    const endpoint = `${this.mapRoutes.list}${wbId}/default-view/`;
    return this.http.Get(endpoint).pipe(
      map(response => {
        const view: View = response['view'];
        return view;
      }),
      catchError((err: HttpErrorResponse) => throwError(err))
    );
  }

  setOpeningType(workbookId: number, openAs: string): Observable<boolean> {
    if (workbookId !== null && typeof workbookId !== 'undefined') {
      const finalEndpoint = `${this.mapRoutes.list}${workbookId}/set-opening-type/`;
      return this.http.Post(finalEndpoint, {open_as: openAs}).pipe(
        map((response) => response['success'])
      );
    } else {
      return of(false);
    }
  }

  setSnapshotUrl(url: string) {
    this.bsSnapshot.next(url);
  }

  observeSnapshotUrl(): Observable<string> {
    return this.bsSnapshot.asObservable();
  }

  getWorkbook(workbookId: number): Observable<Workbook> {
    return this.http.Get(`${this.mapRoutes.list}${workbookId}/`);
  }

  synchronizeWorkbook(workbookId: number) {
    return this.http.Get(`${this.mapRoutes.list}${workbookId}/sync-workbook/`);
  }
}
